import { Center, Stack, Title, Alert, Button, Group, Paper, Text } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons';
import clone from 'clone';
import React, { useMemo, useState } from 'react';
import { NavLink, useLoaderData } from 'react-router-dom';
import FloatingLabelInput from '../../Components/Input';
import { EditEntityLoaderResult } from '../../Loaders/EditEntityLoader';
import { Entity } from '../../../backend/models/entity';
import { Section } from '../../../backend/models/section';
import { Ring } from '../../../backend/models/ring';

export type EntityType = 'Ring' | 'Section';

type PropType = {
  type: EntityType;
  updateEntity: (update: Ring | Section) => Promise<Response>;
};

const EntityAdmin = (props: PropType) => {
  const { entity, entities, technologies } = useLoaderData() as EditEntityLoaderResult;
  const [initialEntity, setInitialEntity] = useState<Entity>(entity);
  const [newName, setNewName] = useState<string>(entity.name);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  const nameExists = useMemo(() => {
    return entities
      .filter((e) => e.id !== entity.id)
      .map((e) => e.name)
      .includes(newName.trim());
  }, [entities, entity, newName]);

  function onSubmit() {
    entity.name = newName.trim();
    props.updateEntity(props.type === 'Ring' ? (entity as Ring) : (entity as Section)).then(async (res) => {
      const ent = (await res.json()) as Entity;
      setInitialEntity(clone(ent));
      setNewName(ent.name);
      setShowSuccess(true);
    });
  }

  return (
    <Center>
      <Stack w={500} maw={'100%'}>
        <Title order={2}>{entity.name}</Title>
        <Group align={'flex-end'} grow>
          <FloatingLabelInput
            label={'New name'}
            value={newName}
            onChange={(e) => {
              setNewName(e.currentTarget.value);
              setShowSuccess(false);
            }}
          />
          <Button onClick={onSubmit} disabled={nameExists || newName.trim() === initialEntity.name}>
            Save
          </Button>
        </Group>
        {showSuccess && <Text color={'green'}>{`${props.type} successfully updated!`}</Text>}
        {nameExists && <Text color={'red'}>{`There is already a ${props.type} with the name ${newName.trim()}!`}</Text>}
        {technologies.length > 0 && (
          <>
            <Alert icon={<IconAlertCircle size={16} />} title="Beware!" color="red">
              The following technologies will have their {props.type} value changed when updating the name
            </Alert>
            {technologies.map((t) => (
              <Paper
                component={NavLink}
                to={`/technology/${encodeURIComponent(t.name)}`}
                p={'xl'}
                withBorder
                shadow={'xs'}
                key={t.name}
              >
                {t.name}
              </Paper>
            ))}
          </>
        )}
      </Stack>
    </Center>
  );
};

export default EntityAdmin;
